<template>
  <section class="school-page">
    <div class="school-hero">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">{{ heroTitle }}</h1>
          <p class="hero-subtitle">
            {{ heroSubtitle }}
          </p>
          <div class="hero-actions">
            <router-link to="/contact" custom v-slot="{ navigate }">
              <Button variant="primary" size="lg" @click="() => { trackCTA('contact'); navigate() }">
                <i class="fas fa-envelope"></i>
                {{ tr('school.heroCtaContact') }}
              </Button>
            </router-link>
            <router-link to="/events" custom v-slot="{ navigate }">
              <Button variant="secondary" size="lg" @click="() => { trackCTA('events'); navigate() }">
                <i class="fas fa-calendar-alt"></i>
                {{ tr('school.heroCtaEvents') }}
              </Button>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Especialidades (fijo) -->
    <div class="specialties-section section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ tr('school.specialtiesTitle') }}</h2>
          <p class="section-subtitle">{{ tr('school.specialtiesSubtitle') }}</p>
        </div>

        <div class="specialties-grid">
          <Card class="specialty-card featured" variant="elevated" padding="lg" hoverable>
            <div class="specialty-icon">
              <i class="fas fa-child"></i>
            </div>
            <h3 class="specialty-title">{{ tr('school.specialtyMovementTitle') }}</h3>
            <p class="specialty-text">
              {{ tr('school.specialtyMovementText') }}
            </p>
          </Card>

          <Card class="specialty-card featured" variant="elevated" padding="lg" hoverable>
            <div class="specialty-icon">
              <i class="fas fa-music"></i>
            </div>
            <h3 class="specialty-title">{{ tr('school.specialtyLanguageTitle') }}</h3>
            <p class="specialty-text">
              {{ tr('school.specialtyLanguageText') }}
            </p>
          </Card>

          <Card class="specialty-card instruments" variant="elevated" padding="lg" hoverable>
            <div class="specialty-icon">
              <i class="fas fa-guitar"></i>
            </div>
            <h3 class="specialty-title">{{ tr('school.specialtyInstrumentsTitle') }}</h3>
            <p class="specialty-text">{{ tr('school.specialtyInstrumentsText') }}</p>
            <div class="instruments-list">
              <span class="instrument-tag">{{ tr('school.instrumentClarinet') }}</span>
              <span class="instrument-tag">{{ tr('school.instrumentSaxophone') }}</span>
              <span class="instrument-tag">{{ tr('school.instrumentFlute') }}</span>
              <span class="instrument-tag">{{ tr('school.instrumentEuphonium') }}</span>
              <span class="instrument-tag">{{ tr('school.instrumentTrombone') }}</span>
              <span class="instrument-tag">{{ tr('school.instrumentTrumpet') }}</span>
              <span class="instrument-tag">{{ tr('school.instrumentPercussion') }}</span>
            </div>
          </Card>
        </div>
      </div>
    </div>


    <!-- Noticias de la escuela (administrable) -->
    <section class="school-news-section section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ $t('school.newsTitle') }}</h2>
          <p class="section-subtitle">{{ $t('school.newsSubtitle') }}</p>
        </div>

        <!-- Search and Filters -->
        <div class="filters-section">
          <div class="search-wrapper">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('school.searchPlaceholder')"
              class="search-input"
            />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-button">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="filter-buttons">
            <button
              @click="selectedCategory = null"
              :class="['filter-button', { active: selectedCategory === null }]"
            >
              {{ $t('news.filterAll') }}
            </button>
            <button
              v-for="category in availableCategories"
              :key="category"
              @click="selectedCategory = category"
              :class="['filter-button', { active: selectedCategory === category }]"
            >
              {{ category }}
            </button>
          </div>
        </div>

        <div class="school-news-content">
          <!-- Loading State -->
          <div v-if="schoolNewsLoading" class="loading-grid">
            <NewsCard v-for="i in 6" :key="i" :newsItem="placeholderItem" :loading="true" />
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredSchoolNews.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-newspaper"></i>
            </div>
            <h3>{{ searchQuery || selectedCategory ? $t('news.noNewsFiltered') : $t('news.noNews') }}</h3>
            <p>{{ searchQuery || selectedCategory ? $t('news.noNewsFilteredDesc') : $t('news.noNewsSoonDesc') }}</p>
            <button v-if="searchQuery || selectedCategory" @click="clearFilters" class="clear-filters-button">
              {{ $t('news.clearFilters') }}
            </button>
          </div>

          <!-- School News Grid -->
          <div v-else>
            <!-- Featured News -->
            <div v-if="featuredSchoolNews.length > 0" class="featured-section">
              <h3 class="section-subtitle">
                <i class="fas fa-star"></i>
                {{ $t('news.featuredNews') }}
              </h3>
              <div class="featured-grid">
                <NewsCard
                  v-for="item in featuredSchoolNews"
                  :key="item.id"
                  :newsItem="item"
                  @click="openSchoolNewsDetail"
                />
              </div>
            </div>

            <!-- Regular News -->
            <div v-if="regularPaginatedSchoolNews.length > 0" class="regular-section">
              <h3 v-if="featuredSchoolNews.length > 0" class="section-subtitle">
                <i class="fas fa-newspaper"></i>
                {{ $t('news.allNews') }}
              </h3>
              <div class="news-grid">
                <NewsCard
                  v-for="item in regularPaginatedSchoolNews"
                  :key="item.id"
                  :newsItem="item"
                  @click="openSchoolNewsDetail"
                />
              </div>

              <!-- Pagination -->
              <div v-if="totalPages > 1" class="pagination">
                <button
                  @click="currentPage = 1"
                  :disabled="currentPage === 1"
                  class="page-button"
                  :aria-label="tr('school.paginationFirst')"
                >
                  <i class="fas fa-angle-double-left"></i>
                </button>
                <button
                  @click="currentPage--"
                  :disabled="currentPage === 1"
                  class="page-button"
                  :aria-label="tr('school.paginationPrev')"
                >
                  <i class="fas fa-angle-left"></i>
                </button>

                <div class="page-numbers">
                  <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="currentPage = page"
                    :class="['page-number', { active: currentPage === page }]"
                  >
                    {{ page }}
                  </button>
                </div>

                <button
                  @click="currentPage++"
                  :disabled="currentPage === totalPages"
                  class="page-button"
                  :aria-label="tr('school.paginationNext')"
                >
                  <i class="fas fa-angle-right"></i>
                </button>
                <button
                  @click="currentPage = totalPages"
                  :disabled="currentPage === totalPages"
                  class="page-button"
                  :aria-label="tr('school.paginationLast')"
                >
                  <i class="fas fa-angle-double-right"></i>
                </button>

                <div class="page-info">
                  {{ $t('news.page') }} {{ currentPage }} {{ $t('news.of') }} {{ totalPages }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- School News Detail Modal -->
    <NewsDetailModal
      v-model="showDetailModal"
      v-if="selectedSchoolNews"
      :newsItem="selectedSchoolNews"
      :shareUrl="getSchoolNewsUrl(selectedSchoolNews)"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useHead } from '@vueuse/head';
import Button from '../ui/Button.vue';
import Card from '../ui/Card.vue';
import { useAnalytics } from '../../composables/useAnalytics';
import { useSchool } from '../../composables/useSchool';
import { useSchoolNews, type SchoolNewsItem } from '../../composables/useSchoolNews';
import { absoluteUrl, buildHeadConfig, getBreadcrumbJsonLd } from '../../seo/meta';
import NewsCard from '@/components/news/NewsCard.vue';
import NewsDetailModal from '@/components/news/NewsDetailModal.vue';

const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

const { trackEvent } = useAnalytics();
const { schoolContent, fetchSchool } = useSchool();

const schoolNewsComposable = useSchoolNews();
const { fetchSchoolNews } = schoolNewsComposable;
const schoolNews = schoolNewsComposable.schoolNews;
const schoolNewsLoading = schoolNewsComposable.loading;

const searchQuery = ref('');
const selectedCategory = ref<string | null>(null);
const currentPage = ref(1);
const itemsPerPage = 6;
const showDetailModal = ref(false);
const selectedSchoolNews = ref<SchoolNewsItem | null>(null);

// Placeholder for skeleton loading
const placeholderItem: SchoolNewsItem = {
  id: 'placeholder',
  title: tr('news.loading'),
  content: tr('news.loading'),
  createdAt: new Date(),
  author: tr('news.loading'),
  featured: false,
  categories: [],
};

const heroTitle = computed(() => schoolContent.value?.heroTitle || tr('school.heroTitleFallback'));
const heroSubtitle = computed(
  () =>
    schoolContent.value?.heroSubtitle ||
    tr('school.heroSubtitleFallback'),
);

// Available categories
const availableCategories = computed(() => {
  const categories = new Set<string>();
  schoolNews.value.forEach(item => {
    item.categories?.forEach(cat => categories.add(cat));
  });
  return Array.from(categories).sort();
});

// Filtered school news
const filteredSchoolNews = computed(() => {
  let filtered = schoolNews.value;

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.content.toLowerCase().includes(query)
    );
  }

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(item =>
      item.categories?.includes(selectedCategory.value!)
    );
  }

  return filtered;
});

// Featured school news (always shown first)
const featuredSchoolNews = computed(() => {
  return filteredSchoolNews.value.filter(item => item.featured);
});

// Regular school news (not featured)
const regularSchoolNews = computed(() => {
  return filteredSchoolNews.value.filter(item => !item.featured);
});

// Pagination for regular school news
const totalPages = computed(() => {
  return Math.ceil(regularSchoolNews.value.length / itemsPerPage);
});

const regularPaginatedSchoolNews = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return regularSchoolNews.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= maxVisible) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }

  return pages;
});

// Reset to page 1 when filters change
watch([searchQuery, selectedCategory], () => {
  currentPage.value = 1;
});

const clearFilters = () => {
  searchQuery.value = '';
  selectedCategory.value = null;
};

const openSchoolNewsDetail = (newsItem: SchoolNewsItem) => {
  selectedSchoolNews.value = newsItem;
  showDetailModal.value = true;
};

const getSchoolNewsUrl = (newsItem: SchoolNewsItem) => {
  return absoluteUrl(`/school#${newsItem.id}`);
};

useHead(
  buildHeadConfig('school', {
    jsonLd: [getBreadcrumbJsonLd('school')],
  }),
);

onMounted(async () => {
  trackEvent('school_page_viewed', 'engagement', 'School Page');
  await Promise.all([fetchSchool(), fetchSchoolNews()]);
});

const trackCTA = (where: string) => {
  trackEvent('school_cta_click', 'engagement', where);
};
</script>

<style scoped>
.school-page {
  background: white;
}

/* Loading and Empty States */
.empty-state {
  background: white;
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-12) var(--spacing-6);
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin: var(--spacing-8) auto;
  max-width: 600px;
}

.empty-icon {
  font-size: 4rem;
  color: var(--color-neutral-400);
  margin-bottom: var(--spacing-4);
}

.empty-state h3 {
  font-size: var(--font-size-xl);
  color: var(--color-neutral-600);
  margin: 0 0 var(--spacing-2) 0;
}

.empty-state p {
  color: var(--color-neutral-500);
  margin: 0 0 var(--spacing-4) 0;
}

.clear-filters-button {
  background: var(--color-primary-600);
  color: white;
  border: none;
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
}

.clear-filters-button:hover {
  background: var(--color-primary-700);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}


.school-hero {
  position: relative;
  min-height: 45vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-music);
  overflow: hidden;
  padding: var(--spacing-16) var(--spacing-4);
}

.school-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 70% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 60%);
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  color: white;
}

.hero-title {
  font-size: clamp(2.6rem, 6vw, 4rem);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--spacing-3) 0;
  font-family: var(--font-family-serif);
  color: white;
}

.hero-subtitle {
  font-size: clamp(1.1rem, 2.4vw, 1.5rem);
  margin: 0 0 var(--spacing-6) 0;
  color: rgba(255, 255, 255, 0.92);
}

.hero-actions {
  display: flex;
  gap: var(--spacing-4);
  justify-content: center;
  flex-wrap: wrap;
}

/* Especialidades Section */
.specialties-section {
  background: white;
  padding: var(--spacing-20) 0;
}

.section-header {
  text-align: center;
  margin-bottom: var(--spacing-12);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.section-title {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin: 0 0 var(--spacing-3) 0;
  font-family: var(--font-family-serif);
}

.section-subtitle {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-6) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  justify-content: center;
}

.section-subtitle i {
  color: var(--color-secondary-500);
}

.specialties-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-8);
  max-width: 1200px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .specialties-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.specialty-card {
  height: 100%;
  text-align: center;
  box-shadow: var(--shadow-lg);
  transition: transform 0.3s ease;
}

.specialty-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.specialty-card.featured {
  background: linear-gradient(135deg, rgba(62, 151, 173, 0.05) 0%, rgba(234, 179, 8, 0.05) 100%);
  border: 1px solid rgba(62, 151, 173, 0.1);
}

.specialty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--gradient-music);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  box-shadow: var(--shadow-xl);
  margin: 0 auto var(--spacing-6) auto;
}

.specialty-title {
  margin: 0 0 var(--spacing-3) 0;
  font-size: var(--font-size-xl);
  color: var(--color-primary-700);
  font-family: var(--font-family-serif);
  font-weight: var(--font-weight-bold);
}

.specialty-text {
  margin: 0 0 var(--spacing-4) 0;
  color: var(--color-text-secondary);
  line-height: 1.7;
  font-size: var(--font-size-base);
}

.instruments-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-2);
  justify-content: center;
  margin-top: var(--spacing-4);
}

.instrument-tag {
  background: var(--color-primary-100);
  color: var(--color-primary-700);
  padding: var(--spacing-1) var(--spacing-3);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--color-primary-200);
  transition: all 0.2s ease;
}

.instrument-tag:hover {
  background: var(--color-primary-200);
  transform: scale(1.05);
}


/* School News Section */
.school-news-section {
  background: var(--color-neutral-50);
  padding: var(--spacing-20) 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-4);
}

/* Filters Section */
.filters-section {
  margin-bottom: var(--spacing-10);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}

.search-wrapper {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: var(--spacing-4);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-neutral-400);
  font-size: var(--font-size-lg);
}

.search-input {
  width: 100%;
  padding: var(--spacing-4) var(--spacing-12) var(--spacing-4) var(--spacing-12);
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-lg);
  transition: var(--transition-all);
  background: white;
  box-shadow: var(--shadow-md);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.clear-button {
  position: absolute;
  right: var(--spacing-4);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-neutral-400);
  cursor: pointer;
  padding: var(--spacing-2);
  border-radius: var(--border-radius-full);
  transition: var(--transition-all);
}

.clear-button:hover {
  background: var(--color-neutral-100);
  color: var(--color-neutral-600);
}

.filter-buttons {
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  justify-content: center;
}

.filter-button {
  padding: var(--spacing-2) var(--spacing-4);
  border: 2px solid var(--color-neutral-300);
  background: white;
  border-radius: var(--border-radius-full);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-sm);
}

.filter-button:hover {
  border-color: var(--color-primary-400);
  background: var(--color-primary-50);
  transform: translateY(-2px);
}

.filter-button.active {
  background: var(--color-primary-600);
  color: white;
  border-color: var(--color-primary-600);
}

.school-news-content {
  min-height: 400px;
}

.loading-grid,
.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-8);
  margin-bottom: var(--spacing-10);
}

.featured-section {
  margin-bottom: var(--spacing-12);
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-8);
}

.regular-section {
  margin-top: var(--spacing-12);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  margin-top: var(--spacing-10);
  flex-wrap: wrap;
}

.page-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: white;
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  color: var(--color-neutral-600);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-base);
}

.page-button:hover:not(:disabled) {
  background: var(--color-primary-50);
  border-color: var(--color-primary-400);
  color: var(--color-primary-600);
  transform: translateY(-2px);
}

.page-button:disabled {
  background: var(--color-neutral-100);
  border-color: var(--color-neutral-200);
  color: var(--color-neutral-300);
  cursor: not-allowed;
  transform: none;
}

.page-numbers {
  display: flex;
  gap: var(--spacing-2);
}

.page-number {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 var(--spacing-2);
  background: white;
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  color: var(--color-neutral-600);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

.page-number:hover {
  background: var(--color-primary-50);
  border-color: var(--color-primary-400);
  color: var(--color-primary-600);
  transform: translateY(-2px);
}

.page-number.active {
  background: var(--color-primary-600);
  border-color: var(--color-primary-600);
  color: white;
  box-shadow: var(--shadow-md);
}

.page-info {
  margin-left: var(--spacing-3);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .loading-grid,
  .news-grid,
  .featured-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .loading-grid,
  .news-grid,
  .featured-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-6);
  }

  .filters-section {
    margin-bottom: var(--spacing-6);
  }

  .search-input {
    font-size: var(--font-size-base);
  }

  .filter-buttons {
    justify-content: flex-start;
  }

  .pagination {
    gap: var(--spacing-1);
  }

  .page-button,
  .page-number {
    width: 36px;
    height: 36px;
    min-width: 36px;
  }

  .page-info {
    width: 100%;
    text-align: center;
    margin: var(--spacing-2) 0 0 0;
    order: -1;
  }
}

/* Refresh 2026 - visual coherence with Home */
.school-page {
  background: transparent;
}

.school-hero {
  min-height: 44vh;
  padding: clamp(2.8rem, 6vw, 5.2rem) var(--spacing-4);
  background:
    radial-gradient(circle at 15% 16%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 42%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
}

.school-hero::before {
  background: radial-gradient(circle at 84% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 45%);
}

.hero-title {
  letter-spacing: var(--letter-spacing-tight);
}

.specialties-section,
.school-news-section {
  background: transparent;
}

.specialty-card,
.empty-state,
.search-input,
.filter-button,
.page-button,
.page-number {
  background: var(--surface-card-strong);
  border-color: rgba(16, 41, 59, 0.16);
}

.specialty-card.featured {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(244, 239, 230, 0.9));
}

.specialty-icon {
  background: linear-gradient(132deg, #173c56 0%, #2f6285 55%, #a77123 100%);
}

.search-input:focus {
  box-shadow: 0 0 0 4px rgba(47, 98, 133, 0.16);
}

.filter-button.active,
.page-number.active {
  background: var(--color-primary-700);
  border-color: var(--color-primary-700);
}

.clear-filters-button {
  background: var(--color-primary-700);
}

.clear-filters-button:hover {
  background: var(--color-primary-800);
}
</style>
